import { useCallback, useMemo } from 'react';

import {
  selectBaseTickSize,
  selectProcessedAsks,
  selectProcessedBids,
  selectTickSize,
  selectViewMode,
} from './selectors';
import { OrderbookViewMode, ProcessedOrderbookEntry } from './types';

import { useOrderbookStore } from './orderbook.store';

/**
 * Hook to access processed orderbook data for a pair
 * Returns asks, bids, and last price with current settings applied
 *
 * Memoizes the processed data to prevent unnecessary rerenders
 */
export function useOrderbookData(pairId: number) {
  // Subscribe to raw data and settings
  const rawOrderbook = useOrderbookStore(state => state.orderbooks[pairId]);
  const settings = useOrderbookStore(state => state.settings[pairId]);
  const isLoading = useOrderbookStore(state => state.loading[pairId] || false);
  const error = useOrderbookStore(state => state.errors[pairId] || null);

  // Memoize processed asks
  const asks = useMemo(() => {
    if (!rawOrderbook || !settings) return [];
    return selectProcessedAsks(useOrderbookStore.getState(), pairId);
  }, [rawOrderbook, settings, pairId]);

  // Memoize processed bids
  const bids = useMemo(() => {
    if (!rawOrderbook || !settings) return [];
    return selectProcessedBids(useOrderbookStore.getState(), pairId);
  }, [rawOrderbook, settings, pairId]);

  const lastPrice = rawOrderbook?.lastPrice || '0';

  return {
    asks,
    bids,
    lastPrice,
    isLoading,
    error,
  };
}

/**
 * Hook to access and manage orderbook settings for a pair
 *
 * Actions use getState() to avoid dependency issues with useCallback
 */
export function useOrderbookSettings(pairId: number) {
  const tickSize = useOrderbookStore(state => selectTickSize(state, pairId));
  const baseTickSize = useOrderbookStore(state => selectBaseTickSize(state, pairId));
  const viewMode = useOrderbookStore(state => selectViewMode(state, pairId));

  const handleSetTickSize = useCallback(
    (newTickSize: number | null) => {
      useOrderbookStore.getState().setTickSize(pairId, newTickSize);
    },
    [pairId],
  );

  const handleSetViewMode = useCallback(
    (mode: OrderbookViewMode) => {
      useOrderbookStore.getState().setViewMode(pairId, mode);
    },
    [pairId],
  );

  return {
    tickSize,
    baseTickSize,
    viewMode,
    setTickSize: handleSetTickSize,
    setViewMode: handleSetViewMode,
  };
}

/**
 * Hook to get individual processed asks list
 * Memoized to prevent unnecessary rerenders
 */
export function useProcessedAsks(pairId: number): ProcessedOrderbookEntry[] {
  const rawOrderbook = useOrderbookStore(state => state.orderbooks[pairId]);
  const settings = useOrderbookStore(state => state.settings[pairId]);

  return useMemo(() => {
    if (!rawOrderbook || !settings) return [];
    return selectProcessedAsks(useOrderbookStore.getState(), pairId);
  }, [rawOrderbook, settings, pairId]);
}

/**
 * Hook to get individual processed bids list
 * Memoized to prevent unnecessary rerenders
 */
export function useProcessedBids(pairId: number): ProcessedOrderbookEntry[] {
  const rawOrderbook = useOrderbookStore(state => state.orderbooks[pairId]);
  const settings = useOrderbookStore(state => state.settings[pairId]);

  return useMemo(() => {
    if (!rawOrderbook || !settings) return [];
    return selectProcessedBids(useOrderbookStore.getState(), pairId);
  }, [rawOrderbook, settings, pairId]);
}
